import * as THREE from 'three';
import { PRIORITY } from '../core/Engine.js';
import { EV } from '../core/EventBus.js';
import { ACTIONS } from '../core/Input.js';
import { clamp, clamp01, lerp, expDamp, damp, remap, smoothstep, smootherstep, DEG } from '../util/math.js';

/**
 * Player controller: capsule-vs-AABB collision, a movement state machine, and
 * the camera rig (bob / sway / lean / land / breath / recoil absorption) in one
 * system so the camera can react to a movement state in the same frame it
 * changes.
 *
 * Movement values are tuned for a modern-military feel rather than realism:
 *  - snappy ground acceleration, momentum-preserving air control
 *  - sprint has a short spin-up so tapping shift does nothing
 *  - crouch is a smooth eye-height blend, and standing up is blocked by ceilings
 *  - slide converts sprint momentum into a steerable, decaying boost
 *  - mantle is a scripted 400 ms arc, not a teleport
 *
 * Collision is a vertical cylinder (RADIUS) resolved by depenetration rather
 * than by axis clamping: a circle-vs-AABB push always produces a real contact
 * normal, so corners do not catch and diagonal wall runs never stall.
 */

const EYE_STAND = 1.68;
const EYE_CROUCH = 1.02;
const EYE_SLIDE = 0.78;
const RADIUS = 0.34;
const STEP_HEIGHT = 0.42;
const HEAD_PAD = 0.14;        // skull above the eye — the collision body top
const GROUND_EPS = 0.035;     // resting-contact tolerance for the swept ground test
const BASE_SENS = 0.0022;
/** Janela entre os dois toques em Shift para engatar a corrida tática. */
const TAC_TAP_WINDOW = 0.30;     // rad per mouse count at "1.0" on a settings slider

// Duração da corrida tática. Ela segura a velocidade cheia por TAC_HOLD e depois
// devolve o excedente ao longo de TAC_FADE — o jogador NÃO para de correr, só
// volta à corrida normal. Durar para sempre fazia da corrida normal código morto.
//
// O relógio é o fôlego (`_exertion`), não um contador novo: `_exertion` já sobe
// com esforço e desce em descanso, já alimenta a respiração da câmera, e um
// segundo contador acabaria dessincronizado dele. Aqui ele passa a ser a barra:
// vai de 0 a 1 em TAC_TOTAL segundos de tática, e `tacSprintLeft` é o que sobra.
const TAC_HOLD = 3.0;            // s de velocidade cheia
const TAC_FADE = 0.6;            // s de queda suave até a corrida normal
const TAC_TOTAL = TAC_HOLD + TAC_FADE;
/** Fração final da barra em que o excedente de velocidade é devolvido. */
const TAC_FADE_FRAC = TAC_FADE / TAC_TOTAL;
// Recarga: rápida andando/parado, lenta em corrida normal ou deslize. Sem custo
// para reengatar, a duração não significaria nada — bastaria bater Shift de novo.
const TAC_RECOVER_REST = 0.26;   // 1/s — ~3,8 s do vazio ao cheio parado
const TAC_RECOVER_WORK = 0.12;   // 1/s — correndo normal a recarga é bem mais lenta
/** Reserva mínima para engatar: sem isto o duplo toque entregaria meio segundo de nada. */
const TAC_MIN_ENGAGE = 0.2;

// Suavização de degrau. A colisão continua subindo/descendo em saltos de degrau
// inteiro — é isso que mantém a física correta —, mas a câmera não pode teleportar
// 27,5 cm num quadro, que é o "de soquinho" que se sente na escada. O salto vira
// uma dívida que a câmera paga ao longo de alguns quadros.
//
// A dívida encolhe a uma VELOCIDADE, não por decaimento puro: num lance contínuo
// o decaimento exponencial sozinho ainda entregava 7 cm de salto por quadro,
// porque cada degrau novo chega antes de o anterior ter sido pago e os dois se
// somam. Com teto de velocidade o pior quadro é sempre teto*dt, degrau isolado ou
// escada inteira. O piso garante que a sobra sempre feche (o proporcional puro
// arrastaria os últimos milímetros por segundos) e o termo proporcional no meio
// é o que faz a chegada ser suave em vez de um corte seco.
//
// O teto acompanha a velocidade do jogador porque a escada não sobe mais devagar
// para quem corre: esta escada é 27,5 cm a cada ~47 cm de avanço, ou seja o corpo
// ganha altura a ~0,6 da velocidade horizontal. Um teto fixo abaixo disso satura,
// a dívida bate no limite e o degrau volta a vazar inteiro para a câmera — que foi
// exatamente o que a primeira medição mostrou.
const STEP_SMOOTH_RATE = 10;        // 1/s — regime proporcional
const STEP_SMOOTH_MAX_SPEED = 2.6;  // m/s — teto mínimo, para quem sobe parado/agachado
const STEP_SMOOTH_SPEED_K = 0.7;    // fração da velocidade horizontal que vira teto
const STEP_SMOOTH_MIN_SPEED = 0.6;  // m/s — piso: a sobra sempre fecha
const STEP_SMOOTH_MAX = 0.8;        // dívida máxima acumulada

/**
 * Colete, dois níveis. O 1 é leve: come menos de cada tiro e acaba rápido. O 2 é
 * placa pesada, leva a maior parte do dano e dura o dobro.
 *
 * `absorb` é a fração do dano que vai para o colete em vez de para a vida. Não é
 * 1.0 de propósito: colete que zera dano vira "quem achou o colete ganha", e o
 * jogador perde o sinal de que está levando tiro. O que o colete compra é tempo.
 */
export const ARMOR_TIERS = [
  null,
  { points: 50, absorb: 0.45 },
  { points: 100, absorb: 0.7 },
];

// O EventBus compartilhado não é arquivo meu para editar, então estes dois nomes
// moram aqui, na mesma convenção `dominio:coisa` do resto. HUD/FX escutam por
// string, como já fazem com qualquer outro evento.
export const EV_ARMOR_BREAK = 'player:armor:break';    // { tier, dir, source }
export const EV_ARMOR_PICKUP = 'player:armor:pickup';  // { tier, armor, armorMax }

export const MoveState = {
  IDLE: 'idle', WALK: 'walk', SPRINT: 'sprint', TACSPRINT: 'tacsprint', CROUCH: 'crouch',
  AIR: 'air', SLIDE: 'slide', MANTLE: 'mantle',
};

export class Player {
  constructor(opts = {}) {
    this.name = 'player';
    this.priority = PRIORITY.MOVEMENT;

    this.position = new THREE.Vector3(0, EYE_STAND, 26); // eye position when standing
    this.velocity = new THREE.Vector3();
    this.yaw = Math.PI;
    this.pitch = 0;

    this.grounded = false;
    this.groundNormal = new THREE.Vector3(0, 1, 0);
    this.state = MoveState.IDLE;
    this.eyeHeight = EYE_STAND;
    this.targetEye = EYE_STAND;

    this.sensitivity = opts.sensitivity ?? BASE_SENS;
    this.adsSensScale = 0.62;
    this.invertY = !!opts.invertY;

    this.health = 100;
    this.maxHealth = 100;
    this.alive = true;
    this._regenDelay = 0;

    // Colete: pontos restantes, capacidade do nível equipado, e o nível (0 = sem).
    // Lidos pelo HUD para desenhar a barra.
    this.armor = 0;
    this.armorMax = 0;
    this.armorTier = 0;

    // tunables
    // A corrida tática é ~22% mais rápida que a normal. Mais que isso e ela vira
    // a única forma de andar; menos e não se sente a diferença que justifica o
    // duplo toque.
    this.speeds = { walk: 4.3, sprint: 7.2, tacsprint: 8.8, crouch: 2.1, ads: 2.9, air: 1.6 };
    this.accelGround = 62;
    this.accelAir = 14;
    this.airControl = 3.1;     // rad/s the velocity vector may be steered mid-air
    this.frictionGround = 11;
    this.jumpSpeed = 5.1;
    this.gravity = 19.6;
    this.slideDrag = 1.85;     // exponential decay of slide momentum, 1/s
    this.slideSteer = 7.0;     // lateral accel while sliding, speed-preserving

    this._sprintCharge = 0;
    this._tacSprint = false;
    this._lastSprintTap = -1;   // segundos do último toque em Shift
    this._tacSprintAir = 0;
    /** 0..1 — quanto ainda resta de corrida tática. O HUD desenha isto. */
    this.tacSprintLeft = 1;
    /** 0..1 — fração do excedente de velocidade tática ainda ativa (1 = cheia). */
    this._tacPower = 0;
    this._slideTime = 0;
    this._slideCooldown = 0;   // was read before being defined — real bug, fixed
    this._coyote = 0;
    this._jumpBuffer = 0;
    this._bobPhase = 0;
    this._stepIndex = 0;
    this._stepDist = 0;
    this._lean = 0;
    this._leanTarget = 0;
    this._fallSpeed = 0;
    this._forcedCrouch = false;
    this._bodyH = EYE_STAND + HEAD_PAD;
    this._groundBox = null;
    this._lastGroundSurface = null;
    this._exertion = 0;
    this._breathPhase = 0;
    this._bobAmt = 0;
    this._landDip = 0;
    this._landVel = 0;
    this._mantle = null;
    this._mantleT = 0;
    this._mantleCam = 0;
    this._prevYaw = this.yaw;
    this._prevPitch = 0;
    /** Dívida vertical da câmera contra a posição de colisão (ver STEP_SMOOTH_RATE). */
    this._stepSmooth = 0;

    // camera offsets applied on top of the eye transform each frame
    this.viewOffset = new THREE.Vector3();
    this.viewRotation = new THREE.Euler(0, 0, 0, 'YXZ');
    /** Additive aim punch from recoil, consumed by the weapon system. */
    this.recoilPitch = 0;
    this.recoilYaw = 0;
    /** Read by the view model: x = strafe/turn, y = vertical/pitch, both -1..1. */
    this.movementSway = new THREE.Vector2();

    this.ads = false;
    this._adsBlend = 0;
    this.baseFov = opts.fov ?? 80;
    this.adsFov = opts.adsFov ?? 55;

    this._tmpBox = new THREE.Box3();
    this._tmpV = new THREE.Vector3();
    this._fwdV = new THREE.Vector3();
    this._evV = new THREE.Vector3();

    // allocation-free collision scratch
    this._pen = { nx: 0, nz: 0, depth: 0 };
    this._resA = this._makeRes();
    this._resB = this._makeRes();
  }

  _makeRes() {
    return { x: 0, z: 0, hit: false, n: 0, nx: new Float32Array(8), nz: new Float32Array(8) };
  }

  init(engine) {
    this.engine = engine;
    this.level = engine.get('level');
    if (this.level) {
      this.position.copy(this.level.playerSpawn);
      this.yaw = this.level.playerSpawnYaw ?? this.yaw;
      this._prevYaw = this.yaw;
    }
    engine.provide('player', this);

    engine.bus.on(EV.DAMAGE, (e) => {
      if (e.target !== this) return;
      this.applyDamage(e.amount, e.dir, e.source);
    });

    // A placa quebrando é informação de combate, não decoração: o jogador precisa
    // saber, sem tirar os olhos do inimigo, que a próxima bala é na vida.
    engine.bus.on(EV_ARMOR_BREAK, () => {
      engine.bus.emit(EV.SOUND, { name: 'knife_wall', volume: 1 });
      engine.bus.emit(EV.NOTIFY, { text: 'colete destruído', kind: 'warn' });
    });

  }

  applyDamage(amount, dir, source) {
    if (!this.alive || !(amount > 0)) return;

    // O colete come sua parte ANTES da vida, e se gasta no que comeu. Queda não
    // passa por ele: colete para tiro, não para chão — e um colete que impedisse
    // dano de queda faria o jogador pular de lugares que não devia.
    let absorbed = 0;
    if (this.armorTier > 0 && this.armor > 0 && source !== 'fall') {
      const tier = ARMOR_TIERS[this.armorTier];
      absorbed = Math.min(this.armor, amount * (tier ? tier.absorb : 0.5));
      this.armor -= absorbed;
      if (this.armor <= 1e-4) {
        // Quebrou: o resto do tiro que quebrou a placa já foi absorvido, mas do
        // próximo em diante é tudo na vida.
        this.armor = 0;
        const broken = this.armorTier;
        this.armorTier = 0;
        this.armorMax = 0;
        this.engine.bus.emit(EV_ARMOR_BREAK, { tier: broken, dir, source });
      }
    }

    const toHealth = amount - absorbed;
    this.health = Math.max(0, this.health - toHealth);
    this._regenDelay = 4.5;
    this.engine.bus.emit(EV.PLAYER_HURT, {
      amount: toHealth, dir, source, health: this.health, absorbed, armor: this.armor,
    });
    if (this.health <= 0) {
      this.alive = false;
      // Morrer perde o colete. O respawn (NetSystem) só devolve vida, então sem
      // isto o jogador renasceria com a placa que já tinha — e o mapa deixaria de
      // ter motivo para ser percorrido.
      this.armor = 0; this.armorTier = 0; this.armorMax = 0;
      this.engine.bus.emit(EV.PLAYER_DIED, { source });
    }
  }

  /**
   * Equipa/reabastece o colete. Devolve quanto de fato entrou, para quem chamou
   * poder decidir se o item foi consumido — pegar um colete que não somou nada
   * é a forma mais barata de irritar o jogador.
   *
   * Nível maior sempre substitui o menor (e reseta os pontos). Nível igual só
   * completa. Nível menor sobre um maior não faz nada.
   */
  giveArmor(tier) {
    const t = ARMOR_TIERS[tier | 0];
    if (!t || !this.alive) return 0;
    if (tier < this.armorTier) return 0;
    if (tier > this.armorTier) {
      const gained = t.points - this.armor;
      this.armorTier = tier;
      this.armorMax = t.points;
      this.armor = t.points;
      this.engine?.bus.emit(EV_ARMOR_PICKUP, { tier, armor: this.armor, armorMax: this.armorMax });
      return gained;
    }
    if (this.armor >= t.points - 1e-4) return 0;
    const gained = t.points - this.armor;
    this.armor = t.points;
    this.armorMax = t.points;
    this.engine?.bus.emit(EV_ARMOR_PICKUP, { tier, armor: this.armor, armorMax: this.armorMax });
    return gained;
  }

  // -------------------------------------------------------------------------
  // Runtime settings — the HUD options panel calls these.
  // -------------------------------------------------------------------------

  /**
   * Accepts either raw radians-per-count (~0.002) or a slider multiplier where
   * 1.0 is the shipped default, because a settings panel almost always hands
   * out the latter and there is no ambiguity between the two ranges.
   */
  setSensitivity(v) {
    if (!Number.isFinite(v) || v <= 0) return this.sensitivity;
    this.sensitivity = clamp(v > 0.05 ? BASE_SENS * v : v, 0.00012, 0.02);
    return this.sensitivity;
  }

  /** Hip-fire vertical FOV. ADS FOV is owned by the weapon system. */
  setFov(v) {
    if (!Number.isFinite(v)) return this.baseFov;
    this.baseFov = clamp(v, 55, 130);
    return this.baseFov;
  }

  setInvertY(b) { this.invertY = !!b; return this.invertY; }

  // -------------------------------------------------------------------------
  // Look — runs on the render frame so mouse input is never sub-sampled.
  // -------------------------------------------------------------------------
  update(dt, alpha, engine) {
    const input = engine.input;
    if (input.locked && this.alive) {
      const sens = this.sensitivity * lerp(1, this.adsSensScale, this._adsBlend);
      const invert = this.invertY ? -1 : 1;
      this.yaw -= input.mouseDX * sens;
      this.pitch -= input.mouseDY * sens * invert;

      // Controller look: exponential curve + frame-rate independent rate.
      if (input.gamepadIndex >= 0) {
        const rx = input.stickR.x, ry = input.stickR.y;
        this.yaw -= rx * Math.abs(rx) * 3.4 * dt;
        this.pitch -= ry * Math.abs(ry) * 2.6 * dt * invert;
      }
      this.pitch = clamp(this.pitch, -89 * DEG, 89 * DEG);
    }

    // Recoil is applied to the view and decays back; the portion the player
    // does not fight is absorbed into yaw/pitch so the gun climbs for real.
    const decay = 1 - Math.exp(-9 * dt);
    this.pitch += this.recoilPitch * decay * 0.35;
    this.yaw += this.recoilYaw * decay * 0.35;
    this.recoilPitch *= 1 - decay;
    this.recoilYaw *= 1 - decay;

    this._updateSway(dt);
    this._updateCamera(dt, engine);
  }

  /**
   * Per-frame sway signal for the view model. Strafing and turning both push the
   * gun; the value is normalized so weapon code can scale it however it likes.
   */
  _updateSway(dt) {
    const d = Math.max(dt, 1e-4);
    const cosY = Math.cos(this.yaw), sinY = Math.sin(this.yaw);
    const lateral = this.velocity.x * cosY - this.velocity.z * sinY;
    const turn = clamp((this.yaw - this._prevYaw) / d * 0.085, -1, 1);
    const tilt = clamp((this.pitch - this._prevPitch) / d * 0.085, -1, 1);
    this._prevYaw = this.yaw;
    this._prevPitch = this.pitch;

    const tx = clamp(clamp(lateral / this.speeds.walk, -1, 1) * 0.55 + turn * 0.85, -1, 1);
    const ty = clamp(clamp(-this.velocity.y / 7, -1, 1) * 0.45 + tilt * 0.85, -1, 1);
    // ADS pins the gun to the eye, so the sway the view model sees shrinks too.
    const scale = lerp(1, 0.3, this._adsBlend);
    this.movementSway.x = expDamp(this.movementSway.x, tx * scale, 11, dt);
    this.movementSway.y = expDamp(this.movementSway.y, ty * scale, 11, dt);
  }

  _updateCamera(dt, engine) {
    const cam = engine.camera;
    // A long frame (tab restore, shader compile) must not blow up the springs.
    dt = clamp(dt, 0, 1 / 30);

    // Eye height easing — slower going down than coming up reads as weight.
    this.eyeHeight = expDamp(this.eyeHeight, this.targetEye, this.targetEye < this.eyeHeight ? 13 : 9, dt);

    // Head bob: figure-eight driven by distance travelled (see fixedUpdate), so
    // the bob, the stride and the footstep audio are the same clock.
    const planar = Math.hypot(this.velocity.x, this.velocity.z);

    // Dívida de degrau: só encolhe aqui, no relógio do quadro renderizado, porque
    // é puramente visual. Quem a cria é a colisão em `_move`/`_tryStep`, e só nos
    // dois casos que são degrau de verdade — queda, pulo e pouso continuam
    // imediatos porque nunca escrevem nada aqui.
    if (this._stepSmooth !== 0) {
      const mag = Math.abs(this._stepSmooth);
      const ceil = Math.max(STEP_SMOOTH_MAX_SPEED, planar * STEP_SMOOTH_SPEED_K);
      const speed = clamp(mag * STEP_SMOOTH_RATE, STEP_SMOOTH_MIN_SPEED, ceil);
      const paid = Math.min(mag, speed * dt);
      this._stepSmooth += this._stepSmooth < 0 ? paid : -paid;
      if (Math.abs(this._stepSmooth) < 1e-4) this._stepSmooth = 0;
    }

    const bobTarget = this.grounded && this.state !== MoveState.SLIDE
      ? clamp01(planar / this.speeds.sprint) : 0;
    this._bobAmt = expDamp(this._bobAmt, bobTarget, bobTarget > this._bobAmt ? 9 : 5, dt);
    const adsDamp = lerp(1, 0.16, this._adsBlend);
    const bobAmt = this._bobAmt * adsDamp;
    const bobX = Math.sin(this._bobPhase) * 0.034 * bobAmt;
    const bobY = (Math.abs(Math.cos(this._bobPhase)) - 0.5) * 0.030 * bobAmt;
    const bobRoll = Math.sin(this._bobPhase) * 0.012 * bobAmt;
    const bobPitch = Math.cos(this._bobPhase * 2) * 0.006 * bobAmt;

    // Landing: an impulse into a critically-ish damped spring. springDamp() in
    // util/math clamps overshoot past the target, which kills an impulse fired
    // from rest, so the spring is integrated here instead.
    const LF = 9.5, LZ = 0.55;
    this._landVel += (-(LF * LF) * this._landDip - 2 * LZ * LF * this._landVel) * dt;
    this._landDip += this._landVel * dt;
    const landY = clamp(this._landDip, -0.34, 0.06);
    const landPitch = landY * 0.42;   // the head nods with the knees

    // Breathing: tiny while walking, real after a sprint, most visible in ADS
    // where the zoom magnifies it. Two frequencies so it never reads as a loop.
    this._breathPhase += dt * (0.85 + this._exertion * 1.9);
    const stillness = 1 - clamp01(planar / 2.4);
    const breathAmp = (0.0035 + this._exertion * 0.020) * stillness * lerp(0.6, 1.3, this._adsBlend);
    const breathY = Math.sin(this._breathPhase) * breathAmp;
    const breathPitch = Math.sin(this._breathPhase * 0.5 + 1.1) * breathAmp * 0.9;
    const breathYaw = Math.sin(this._breathPhase * 0.37) * breathAmp * 0.7;

    // Lean
    this._lean = damp(this._lean, this._leanTarget, 0.9, dt);
    const leanRoll = this._lean * 16 * DEG;
    const leanShift = this._lean * 0.42;

    // Slide tilts the camera into the direction of travel; strafing tilts a
    // touch the other way. Both are the same channel so they cannot fight.
    const slideRoll = this.state === MoveState.SLIDE ? 5 * DEG : 0;
    const strafeRoll = -this.movementSway.x * 1.5 * DEG;

    // Mantle arc: chin drops toward the ledge, then the body swings over it.
    const mantleArc = this._mantle ? Math.sin(this._mantleT * Math.PI) : 0;
    this._mantleCam = expDamp(this._mantleCam, mantleArc, 18, dt);
    const mantlePitch = -this._mantleCam * 9 * DEG;
    const mantleRoll = this._mantleCam * 4.5 * DEG * (this._mantleSign || 1);

    cam.position.set(this.position.x, this.position.y - EYE_STAND + this.eyeHeight, this.position.z);
    cam.position.x += bobX * Math.cos(this.yaw) + leanShift * Math.cos(this.yaw);
    cam.position.z += -bobX * Math.sin(this.yaw) - leanShift * Math.sin(this.yaw);
    cam.position.y += bobY + landY + breathY - this._stepSmooth;
    cam.position.add(this.viewOffset);

    cam.rotation.set(
      this.pitch + bobPitch + landPitch + breathPitch + mantlePitch + this.viewRotation.x,
      this.yaw + breathYaw + this.viewRotation.y,
      bobRoll + leanRoll + slideRoll + strafeRoll + mantleRoll + this.viewRotation.z,
      'YXZ',
    );

    // FOV: ADS pulls in, sprint pushes out slightly. Both eased, never snapped.
    // O extra da tática acompanha a barra pelo mesmo `_tacPower` da velocidade:
    // o campo de visão abre junto com o excedente e fecha junto com ele, em vez
    // de dar um pulo de 3,5 graus na hora em que o estado troca.
    const sprintKick = this.state === MoveState.TACSPRINT ? lerp(4.5, 8, this._tacPower) :
      this.state === MoveState.SPRINT ? 4.5 : (this.state === MoveState.SLIDE ? 7 : 0);
    const targetFov = lerp(this.baseFov + sprintKick, this.adsFov, this._adsBlend);
    if (Math.abs(cam.fov - targetFov) > 0.01) {
      cam.fov = expDamp(cam.fov, targetFov, 14, dt);
      cam.updateProjectionMatrix();
    }
  }

  // -------------------------------------------------------------------------
  // Simulation — fixed step
  // -------------------------------------------------------------------------
  fixedUpdate(dt, engine) {
    if (!this.alive) { this.velocity.multiplyScalar(0.9); return; }
    const input = engine.input;

    // --- health regen -------------------------------------------------------
    this._regenDelay = Math.max(0, this._regenDelay - dt);
    if (this._regenDelay === 0 && this.health < this.maxHealth) {
      this.health = Math.min(this.maxHealth, this.health + 32 * dt);
    }

    this._slideCooldown = Math.max(0, this._slideCooldown - dt);

    // Mantle owns the body outright: no input, no gravity, no collision.
    if (this._mantle) {
      this._adsBlend = expDamp(this._adsBlend, 0, 16, dt);
      this.ads = false;
      this._leanTarget = 0;
      this._sprintCharge = 0;
      this._bodyH = expDamp(this._bodyH, this.targetEye + HEAD_PAD, 14, dt);
      this._updateMantle(dt, engine);
      return;
    }

    // --- intent -------------------------------------------------------------
    const mv = input.moveVector();
    const wishLenRaw = Math.hypot(mv.x, mv.y);
    const feet = this.position.y - EYE_STAND;

    // Standing up is a request, not a right: a ceiling keeps you crouched.
    const held = input.isDown(ACTIONS.CROUCH);
    this._forcedCrouch = !held && this.targetEye < EYE_STAND &&
      this._ceilingBlocked(this.position.x, this.position.z, feet, EYE_STAND + HEAD_PAD);
    const wantCrouch = held || this._forcedCrouch;

    const wantSprint = input.isDown(ACTIONS.SPRINT) && mv.y > 0.25 && !this.ads &&
      !wantCrouch && this._slideTime <= 0;
    this._sprintCharge = wantSprint ? Math.min(1, this._sprintCharge + dt * 6) : 0;

    // Corrida tática: dois toques em Shift dentro da janela. O segundo toque é
    // detectado na BORDA, não no estado pressionado — segurar Shift o tempo todo
    // nunca deve escalar sozinho para tática, senão o duplo toque não significa
    // nada e o jogador perde o controle de qual das duas corridas está usando.
    const agora = this.engine.time.elapsed;
    if (input.wasPressed(ACTIONS.SPRINT)) {
      // Só engata com fôlego suficiente para valer a pena — engatar no fim da
      // barra daria meio segundo de tática e um decaimento imediato, que o
      // jogador leria como bug, não como cansaço.
      if (agora - this._lastSprintTap < TAC_TAP_WINDOW && this.tacSprintLeft >= TAC_MIN_ENGAGE) {
        this._tacSprint = true;
      }
      this._lastSprintTap = agora;
    }
    // Sai da tática por qualquer coisa que quebre a corrida em linha reta: soltar
    // Shift, parar de ir para frente, mirar, agachar ou sair do chão.
    if (!wantSprint || (!this.grounded && this._tacSprintAir > 0.35)) this._tacSprint = false;
    this._tacSprintAir = this.grounded ? 0 : (this._tacSprintAir || 0) + dt;

    // ADS is refused mid-slide and mid-mantle; anywhere else it is the player's
    // call. (The old `this.grounded !== null` test was always true.)
    const wantAds = input.isDown(ACTIONS.ADS) && this._slideTime <= 0;
    this.ads = wantAds;
    this._adsBlend = expDamp(this._adsBlend, wantAds ? 1 : 0, 16, dt);
    this._leanTarget = (input.isDown(ACTIONS.LEAN_R) ? 1 : 0) - (input.isDown(ACTIONS.LEAN_L) ? 1 : 0);

    // --- slide entry / exit -------------------------------------------------
    const planarNow = Math.hypot(this.velocity.x, this.velocity.z);
    if (this._slideTime <= 0 && this.grounded && this._slideCooldown <= 0 &&
        input.wasPressed(ACTIONS.CROUCH) && planarNow > this.speeds.walk * 1.08) {
      this._startSlide(engine, planarNow);
    }
    if (this._slideTime > 0) {
      this._slideTime = Math.max(0, this._slideTime - dt);
      const standing = !held && !this._ceilingBlocked(this.position.x, this.position.z, feet, EYE_STAND + HEAD_PAD);
      // A slide ends on its own terms: out of time, out of speed, off the
      // ground, or the player stood up (and had the headroom to).
      if (this._slideTime <= 0 || planarNow < 2.7 || !this.grounded || standing) this._slideTime = 0;
    }

    // --- state machine ------------------------------------------------------
    if (!this.grounded) {
      this.state = MoveState.AIR;
    } else if (this._slideTime > 0) {
      this.state = MoveState.SLIDE;
    } else if (wantCrouch) {
      this.state = MoveState.CROUCH;
    } else if (this._sprintCharge >= 1) {
      this.state = this._tacSprint ? MoveState.TACSPRINT : MoveState.SPRINT;
    } else if (wishLenRaw > 0.05) {
      this.state = MoveState.WALK;
    } else {
      this.state = MoveState.IDLE;
    }

    // O fôlego é ao mesmo tempo a barra da corrida tática e o que faz a câmera
    // respirar. Só a tática gasta: correr normal, deslizar, andar ou parar
    // recupera — é o que garante que a duração da tática signifique alguma coisa
    // sem transformar a corrida comum numa punição.
    if (this.state === MoveState.TACSPRINT) {
      this._exertion = clamp01(this._exertion + dt / TAC_TOTAL);
      // Barra no fim: a tática se desengata sozinha e a corrida normal continua.
      // O jogador não para de correr, só perde o excedente — e neste instante os
      // valores dos dois estados já coincidem (_tacPower é 0), então não há degrau.
      if (this._exertion >= 1) { this._tacSprint = false; this.state = MoveState.SPRINT; }
    } else {
      const trabalhando = this.state === MoveState.SPRINT || this.state === MoveState.SLIDE;
      this._exertion = clamp01(this._exertion -
        dt * (trabalhando ? TAC_RECOVER_WORK : TAC_RECOVER_REST));
    }
    this.tacSprintLeft = clamp01(1 - this._exertion);
    // Suavização nas duas pontas: smoothstep tem derivada zero em 0 e em 1, então
    // nem a entrada no decaimento nem a chegada na velocidade normal têm quina.
    this._tacPower = smoothstep(this.tacSprintLeft / TAC_FADE_FRAC);

    let wantEye = this.state === MoveState.SLIDE ? EYE_SLIDE
      : (wantCrouch ? EYE_CROUCH : EYE_STAND);
    if (wantEye > this.targetEye &&
        this._ceilingBlocked(this.position.x, this.position.z, feet, wantEye + HEAD_PAD)) {
      wantEye = this.targetEye;
    }
    this.targetEye = wantEye;
    this._bodyH = expDamp(this._bodyH, this.targetEye + HEAD_PAD, 14, dt);

    // --- horizontal movement ------------------------------------------------
    const sin = Math.sin(this.yaw), cos = Math.cos(this.yaw);
    // forward is -Z rotated by yaw
    const wishX = mv.x * cos - mv.y * sin;
    const wishZ = -mv.x * sin - mv.y * cos;
    const wishLen = Math.hypot(wishX, wishZ);

    if (this.state === MoveState.SLIDE) {
      this._slideMove(dt, wishX, wishZ, wishLen);
    } else {
      this._walkMove(dt, wishX, wishZ, wishLen);
    }

    // --- jump / mantle ------------------------------------------------------
    this._coyote = this.grounded ? 0.12 : Math.max(0, this._coyote - dt);
    if (input.wasPressed(ACTIONS.JUMP)) this._jumpBuffer = 0.14;
    this._jumpBuffer = Math.max(0, this._jumpBuffer - dt);

    // A ledge in front always beats a jump: pressing jump at a wall should pull
    // you over it, not bounce you off it.
    const reachingUp = this._jumpBuffer > 0 || (!this.grounded && input.isDown(ACTIONS.JUMP));
    if (reachingUp && mv.y > 0.15 && this._tryMantle(engine)) {
      this._jumpBuffer = 0;
      return;
    }

    if (this._jumpBuffer > 0 && this._coyote > 0) {
      this.velocity.y = this.jumpSpeed;
      this._jumpBuffer = 0; this._coyote = 0; this.grounded = false;
      // Jumping out of a slide keeps the boost — that is the whole combo.
      if (this._slideTime > 0) { this._slideTime = 0; this._slideCooldown = 0.5; }
      engine.bus.emit(EV.JUMP, { position: this.position.clone() });
    }

    // --- gravity + integrate ------------------------------------------------
    this.velocity.y -= this.gravity * dt;
    this.velocity.y = Math.max(this.velocity.y, -55);
    this._fallSpeed = this.velocity.y;

    this._move(this.velocity.x * dt, this.velocity.y * dt, this.velocity.z * dt, engine);

    // --- stride: bob phase and footsteps share one clock --------------------
    if (this.grounded) {
      const planar = Math.hypot(this.velocity.x, this.velocity.z);
      const stride = this.state === MoveState.TACSPRINT ? 2.3
        : this.state === MoveState.SPRINT ? 2.05
        : this.state === MoveState.CROUCH ? 1.5 : 1.7;
      this._stepDist += planar * dt;
      this._bobPhase += (planar * dt) * (Math.PI / stride);
      // Footfall lands where the vertical bob bottoms out: phase = pi/2 + k*pi.
      const idx = Math.floor((this._bobPhase - Math.PI * 0.5) / Math.PI);
      if (idx !== this._stepIndex) {
        this._stepIndex = idx;
        if (planar > 0.6 && this.state !== MoveState.SLIDE) {
          this._stepDist = 0;
          engine.bus.emit(EV.FOOTSTEP, {
            position: this.position.clone(), speed: planar,
            surface: this._surfaceBelow(),
          });
        }
      }
      if (this._bobPhase > 1e5) { this._bobPhase = 0; this._stepIndex = 0; }
    }
  }

  // -------------------------------------------------------------------------
  // Ground / air acceleration
  // -------------------------------------------------------------------------

  /** Per-state acceleration. Heavier legs at full sprint, light in a crouch. */
  _accel() {
    if (!this.grounded) return this.accelAir;
    // A velocidade terminal sai do equilíbrio entre aceleração e atrito, não do
    // teto em `speeds` — com atrito 11 nenhuma corrida alcança o valor declarado.
    // Por isso a tática precisa de aceleração MAIOR que a normal para de fato ser
    // mais rápida: a primeira tentativa deu a ela uma penalidade de manobra aqui
    // e o resultado foi uma corrida tática mais lenta que a comum. O custo dela
    // fica onde não atrapalha a velocidade: fôlego, arma baixada e um tempo maior
    // para voltar a poder atirar.
    // A tática interpola ENTRE os dois regimes conforme a barra acaba, e não
    // troca de estado de uma vez: como a velocidade terminal sai deste par
    // (aceleração e teto), mudar só um dos dois deixaria um degrau na curva.
    if (this.state === MoveState.TACSPRINT) return this.accelGround * lerp(0.74, 1.15, this._tacPower);
    if (this.state === MoveState.SPRINT) return this.accelGround * 0.74;
    if (this.state === MoveState.CROUCH) return this.accelGround * 0.58;
    return this.ads ? this.accelGround * 0.86 : this.accelGround;
  }

  _maxSpeed() {
    if (this.state === MoveState.TACSPRINT) {
      return lerp(this.speeds.sprint, this.speeds.tacsprint, this._tacPower);
    }
    if (this.state === MoveState.SPRINT) return this.speeds.sprint;
    if (this.state === MoveState.CROUCH) return this.speeds.crouch;
    if (this.ads) return this.speeds.ads;
    return this.speeds.walk;
  }

  _walkMove(dt, wishX, wishZ, wishLen) {
    const maxSpeed = this._maxSpeed();
    const accel = this._accel();
    const curX = this.velocity.x, curZ = this.velocity.z;
    const speed = Math.hypot(curX, curZ);

    if (wishLen > 0.001) {
      const dirX = wishX / wishLen, dirZ = wishZ / wishLen;
      // Quake-style: only accelerate up to maxSpeed along the wish direction,
      // which preserves strafe momentum instead of hard-clamping it.
      const proj = curX * dirX + curZ * dirZ;
      const add = clamp(maxSpeed * wishLen - proj, 0, accel * dt);
      this.velocity.x += dirX * add;
      this.velocity.z += dirZ * add;

      // Air control: redirect the existing speed toward the wish direction
      // without adding energy, so a jump can be steered but never accelerated.
      if (!this.grounded && speed > 0.6) {
        const vdx = curX / speed, vdz = curZ / speed;
        const dot = vdx * dirX + vdz * dirZ;
        if (dot > -0.25) {
          const t = clamp(this.airControl * dt * wishLen, 0, 1);
          let nx = vdx + (dirX - vdx) * t, nz = vdz + (dirZ - vdz) * t;
          const nl = Math.hypot(nx, nz) || 1;
          this.velocity.x = (nx / nl) * speed;
          this.velocity.z = (nz / nl) * speed;
        }
      }
    }

    if (this.grounded && speed > 0.001) {
      // Deceleration curve: releasing the stick stops you hard, counter-strafing
      // stops you harder, holding a direction barely brakes at all.
      let brake = 0.5;
      if (wishLen < 0.01) brake = 1.5;
      else {
        const dot = (curX * wishX + curZ * wishZ) / (speed * wishLen);
        brake = dot < -0.2 ? 1.9 : lerp(0.5, 0.85, clamp01(-dot));
      }
      const drop = Math.max(speed, 2.5) * this.frictionGround * brake * dt;
      const scale = Math.max(0, speed - drop) / speed;
      this.velocity.x *= scale; this.velocity.z *= scale;
    }
  }

  _startSlide(engine, planar) {
    this._slideTime = 1.05;
    this._slideCooldown = 1.1;
    // The boost goes along the direction of travel, not the direction you are
    // looking, so a slide never snaps your momentum sideways.
    const dirX = this.velocity.x / planar, dirZ = this.velocity.z / planar;
    const boost = clamp(this.speeds.sprint * 1.28 - planar, 0, 3.4);
    this.velocity.x += dirX * boost;
    this.velocity.z += dirZ * boost;
    this._exertion = clamp01(this._exertion + 0.12);
    engine.bus.emit(EV.SLIDE_START, { position: this.position.clone(), speed: planar + boost });
  }

  _slideMove(dt, wishX, wishZ, wishLen) {
    const vx = this.velocity.x, vz = this.velocity.z;
    const sp = Math.hypot(vx, vz);
    if (sp < 1e-4) return;
    const dirX = vx / sp, dirZ = vz / sp;

    if (wishLen > 0.001) {
      // Steering is purely lateral and speed-preserving: you can carve the
      // slide around a corner but never pump it for free momentum.
      const wx = wishX / wishLen, wz = wishZ / wishLen;
      const along = wx * dirX + wz * dirZ;
      const latX = wx - dirX * along, latZ = wz - dirZ * along;
      let nx = vx + latX * this.slideSteer * dt;
      let nz = vz + latZ * this.slideSteer * dt;
      const nl = Math.hypot(nx, nz) || 1;
      this.velocity.x = (nx / nl) * sp;
      this.velocity.z = (nz / nl) * sp;
    }

    // Momentum decays exponentially, and the last stretch drags harder so the
    // slide ends on a clear beat instead of trailing off.
    const tail = this._slideTime < 0.35 ? 2.1 : 1;
    const k = Math.exp(-this.slideDrag * tail * dt);
    this.velocity.x *= k;
    this.velocity.z *= k;
  }

  // -------------------------------------------------------------------------
  // Mantle
  // -------------------------------------------------------------------------

  /**
   * Looks for a ledge at chest height in front with clear space above it. On a
   * hit the body is handed to `_updateMantle` for ~400 ms of scripted motion.
   */
  _tryMantle(engine) {
    const colliders = this.level?.colliders;
    if (!colliders || this._mantle || this._slideTime > 0) return false;

    const f = this._forward(this._fwdV);
    const feet = this.position.y - EYE_STAND;
    const reach = RADIUS + 0.5;
    const px = this.position.x + f.x * reach;
    const pz = this.position.z + f.z * reach;

    const minTop = feet + 0.45;                 // below this you just step up
    const maxTop = feet + 1.9;                  // above this you cannot reach
    let best = -Infinity, bestBox = null;
    for (let i = 0; i < colliders.length; i++) {
      const b = colliders[i];
      if (px < b.min.x - 0.02 || px > b.max.x + 0.02) continue;
      if (pz < b.min.z - 0.02 || pz > b.max.z + 0.02) continue;
      const t = b.max.y;
      if (t < minTop || t > maxTop || t <= best) continue;
      if (b.min.y > feet + 1.0) continue;       // a floating slab is not a ledge
      best = t; bestBox = b;
    }
    if (!bestBox) return false;

    // Room to pull up over the lip, and room to stand where we land.
    const lx = this.position.x + f.x * (reach + 0.5);
    const lz = this.position.z + f.z * (reach + 0.5);
    if (this._bodyBlockedAt(px, best + 0.06, pz, 1.0, colliders, bestBox)) return false;
    if (this._bodyBlockedAt(lx, best + 0.06, lz, EYE_CROUCH + HEAD_PAD, colliders, bestBox)) return false;

    const rise = best - feet;
    this._mantle = {
      t: 0,
      dur: clamp(0.34 + rise * 0.11, 0.34, 0.58),
      sx: this.position.x, sy: this.position.y, sz: this.position.z,
      ex: lx, ey: best + EYE_STAND, ez: lz,
      surface: this.surfaceOfBox(bestBox),
    };
    this._mantleSign = this.velocity.x * f.z - this.velocity.z * f.x >= 0 ? 1 : -1;
    this._mantleT = 0;
    this.state = MoveState.MANTLE;
    this.grounded = false;
    this.velocity.set(0, 0, 0);
    this._slideTime = 0;
    engine.bus.emit(EV.MANTLE, {
      position: this.position.clone(),
      target: new THREE.Vector3(lx, best + EYE_STAND, lz),
      height: rise,
      duration: this._mantle.dur,
    });
    return true;
  }

  _updateMantle(dt, engine) {
    const m = this._mantle;
    m.t += dt;
    const t = clamp01(m.t / m.dur);
    this._mantleT = t;

    // Vertical resolves first (the pull), horizontal follows (the swing over
    // the lip). Overlapping them is what stops it reading as a slide up a ramp.
    const up = smootherstep(clamp01(t / 0.62));
    const fwd = smoothstep(clamp01((t - 0.28) / 0.72));
    this.position.x = lerp(m.sx, m.ex, fwd);
    this.position.z = lerp(m.sz, m.ez, fwd);
    this.position.y = lerp(m.sy, m.ey, up) + Math.sin(Math.PI * t) * 0.06;
    // Knees tuck on the way up and extend on the way out.
    this.targetEye = lerp(EYE_STAND, EYE_CROUCH + 0.1, Math.sin(Math.PI * t));

    if (m.t >= m.dur) {
      this.position.set(m.ex, m.ey, m.ez);
      this.targetEye = EYE_STAND;
      this.grounded = true;
      this.state = MoveState.IDLE;
      this._lastGroundSurface = m.surface;
      this._groundBox = null;
      this._coyote = 0.12;
      const f = this._forward(this._fwdV);
      this.velocity.set(f.x * 1.5, 0, f.z * 1.5);
      this._mantle = null;
      this._mantleT = 0;
      this._exertion = clamp01(this._exertion + 0.1);
      engine.bus.emit(EV.LAND, { position: this.position.clone(), impact: 1.5 });
    }
  }

  _forward(out) {
    return out.set(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
  }

  _surfaceBelow() {
    if (this._lastGroundSurface) return this._lastGroundSurface;
    // Ground plane outside the courtyard is sand, inside is asphalt.
    const { x, z } = this.position;
    return Math.abs(x) < 23 && Math.abs(z) < 23 ? 'asphalt' : 'sand';
  }

  // -------------------------------------------------------------------------
  // Collision
  // -------------------------------------------------------------------------

  /**
   * Circle-vs-AABB in XZ. Fills `this._pen` with the shortest push-out normal
   * and depth. Using the true closest-point normal (rather than clamping one
   * axis at a time) is what keeps corners from catching the capsule.
   */
  _penetrationXZ(b, px, pz, r) {
    const cx = px < b.min.x ? b.min.x : (px > b.max.x ? b.max.x : px);
    const cz = pz < b.min.z ? b.min.z : (pz > b.max.z ? b.max.z : pz);
    const dx = px - cx, dz = pz - cz;
    const d2 = dx * dx + dz * dz;
    if (d2 > 1e-10) {
      if (d2 >= r * r) return false;
      const d = Math.sqrt(d2);
      this._pen.nx = dx / d; this._pen.nz = dz / d; this._pen.depth = r - d;
      return true;
    }
    // Centre inside the box — leave through the nearest face.
    const l = px - b.min.x, ri = b.max.x - px, n = pz - b.min.z, fa = b.max.z - pz;
    const m = Math.min(l, ri, n, fa);
    if (m === l) { this._pen.nx = -1; this._pen.nz = 0; this._pen.depth = l + r; }
    else if (m === ri) { this._pen.nx = 1; this._pen.nz = 0; this._pen.depth = ri + r; }
    else if (m === n) { this._pen.nx = 0; this._pen.nz = -1; this._pen.depth = n + r; }
    else { this._pen.nx = 0; this._pen.nz = 1; this._pen.depth = fa + r; }
    return true;
  }

  /**
   * Depenetrates a circle at (px,pz) against every collider whose vertical span
   * overlaps [feetY, topY], re-testing over several passes so a corner formed by
   * two boxes resolves against both instead of squeezing the player through.
   */
  _resolveXZ(px, pz, feetY, topY, colliders, res) {
    res.x = px; res.z = pz; res.hit = false; res.n = 0;
    for (let pass = 0; pass < 4; pass++) {
      let moved = false;
      for (let i = 0; i < colliders.length; i++) {
        const b = colliders[i];
        if (feetY >= b.max.y - 0.002 || topY <= b.min.y + 0.002) continue;
        if (!this._penetrationXZ(b, res.x, res.z, RADIUS)) continue;
        const p = this._pen;
        res.x += p.nx * (p.depth + 1e-4);
        res.z += p.nz * (p.depth + 1e-4);
        res.hit = true; moved = true;
        if (res.n < 8) { res.nx[res.n] = p.nx; res.nz[res.n] = p.nz; res.n++; }
      }
      if (!moved) break;
    }
    return res.hit;
  }

  /** Any solid in the band between the current body top and `wantHeight`. */
  _ceilingBlocked(px, pz, feetY, wantHeight) {
    const colliders = this.level?.colliders;
    if (!colliders) return false;
    const top = feetY + this._bodyH;
    const want = feetY + wantHeight;
    if (want <= top + 0.002) return false;
    for (let i = 0; i < colliders.length; i++) {
      const b = colliders[i];
      if (b.min.y >= want - 0.002 || b.max.y <= top + 0.002) continue;
      if (this._penetrationXZ(b, px, pz, RADIUS - 0.03)) return true;
    }
    return false;
  }

  /** Would a body of `height` standing at (px, feetY, pz) be inside something? */
  _bodyBlockedAt(px, feetY, pz, height, colliders, ignore) {
    const top = feetY + height;
    for (let i = 0; i < colliders.length; i++) {
      const b = colliders[i];
      if (b === ignore) continue;
      if (feetY >= b.max.y - 0.002 || top <= b.min.y + 0.002) continue;
      if (this._penetrationXZ(b, px, pz, RADIUS - 0.04)) return true;
    }
    return false;
  }

  /**
   * Vertical first, then horizontal. Vertical is a swept test — a surface only
   * catches you if you were above it before the step — so falling fast past a
   * ledge no longer snaps you onto its top.
   */
  _move(dx, dy, dz, engine) {
    const colliders = this.level?.colliders;
    if (!colliders) { this.position.set(this.position.x + dx, this.position.y + dy, this.position.z + dz); return; }

    const wasGrounded = this.grounded;
    this.grounded = false;
    this._groundBox = null;

    // --- Y ------------------------------------------------------------------
    const prevFeet = this.position.y - EYE_STAND;
    const prevTop = prevFeet + this._bodyH;
    this.position.y += dy;
    let feet = this.position.y - EYE_STAND;
    let top = feet + this._bodyH;

    if (dy <= 0) {
      let best = -Infinity, bestBox = null;
      for (let i = 0; i < colliders.length; i++) {
        const b = colliders[i];
        if (b.max.y <= best) continue;
        if (b.max.y > feet + 1e-4 && prevFeet >= b.max.y - GROUND_EPS &&
            this._penetrationXZ(b, this.position.x, this.position.z, RADIUS)) {
          best = b.max.y; bestBox = b;
        }
      }
      if (feet < 0 && best < 0) { best = 0; bestBox = null; }
      if (best > -Infinity) {
        this.position.y = best + EYE_STAND;
        this.velocity.y = 0;
        this.grounded = true;
        this._setGround(bestBox);
      }
    } else {
      let low = Infinity;
      for (let i = 0; i < colliders.length; i++) {
        const b = colliders[i];
        if (b.min.y >= low) continue;
        if (b.min.y < top && prevTop <= b.min.y + 0.02 &&
            this._penetrationXZ(b, this.position.x, this.position.z, RADIUS)) {
          low = b.min.y;
        }
      }
      if (low < Infinity) {
        this.position.y = low - this._bodyH + EYE_STAND - 0.002;
        this.velocity.y = Math.min(0, this.velocity.y);
      }
    }

    // Walking off a shallow step should not launch you: if we were grounded and
    // are not climbing, glue back down to anything within one step height.
    if (!this.grounded && wasGrounded && this.velocity.y <= 0.01 && !this._mantle) {
      feet = this.position.y - EYE_STAND;
      let best = -Infinity, bestBox = null;
      for (let i = 0; i < colliders.length; i++) {
        const b = colliders[i];
        if (b.max.y <= best || b.max.y > feet + 1e-4 || b.max.y < feet - STEP_HEIGHT) continue;
        if (this._penetrationXZ(b, this.position.x, this.position.z, RADIUS)) { best = b.max.y; bestBox = b; }
      }
      if (best < 0 && feet - STEP_HEIGHT <= 0 && feet >= 0) { best = 0; bestBox = null; }
      if (best > -Infinity) {
        // Descer degrau: quem estava no chão e continua no chão desceu um degrau,
        // não caiu — o ramo só alcança STEP_HEIGHT. A câmera fica para trás e
        // desce amortecida. Uma queda de verdade não passa por aqui.
        this._addStepSmooth(best - feet);
        this.position.y = best + EYE_STAND;
        this.velocity.y = 0;
        this.grounded = true;
        this._setGround(bestBox);
      }
    }

    if (!wasGrounded && this.grounded) {
      const impact = Math.abs(this._fallSpeed);
      // Impulse into the landing spring — the dip is a compression, not a lerp.
      this._landVel -= remap(impact, 2.2, 17, 0.35, 3.2);
      engine.bus.emit(EV.LAND, { position: this.position.clone(), impact });
      if (impact > 13) {
        const dmg = clamp((impact - 13) * 5.5, 0, 100);
        if (dmg > 1) this.applyDamage(dmg, null, 'fall');
      }
    }
    if (!this.grounded) this._lastGroundSurface = null;

    // --- XZ -----------------------------------------------------------------
    if (dx !== 0 || dz !== 0) {
      feet = this.position.y - EYE_STAND;
      top = feet + this._bodyH;
      const startX = this.position.x, startZ = this.position.z;
      const a = this._resAv = this._resolveInto(startX + dx, startZ + dz, feet, top, colliders, this._resA);

      let committed = a;
      if (a.hit && this.grounded) {
        const stepped = this._tryStep(startX, startZ, dx, dz, feet, colliders, a);
        if (stepped) committed = null;      // _tryStep already wrote the position
      }
      if (committed) {
        this.position.x = committed.x;
        this.position.z = committed.z;
        this._clipVelocity(committed);
      }
    }

    // Keep the player inside the arena even if a collider is missed.
    this.position.x = clamp(this.position.x, -33.5, 33.5);
    this.position.z = clamp(this.position.z, -33.5, 33.5);
  }

  _resolveInto(px, pz, feet, top, colliders, res) {
    this._resolveXZ(px, pz, feet, top, colliders, res);
    return res;
  }

  /** Zero the velocity components that point into the surfaces we hit. */
  _clipVelocity(res) {
    for (let i = 0; i < res.n; i++) {
      const nx = res.nx[i], nz = res.nz[i];
      const d = this.velocity.x * nx + this.velocity.z * nz;
      if (d < 0) { this.velocity.x -= nx * d; this.velocity.z -= nz * d; }
    }
  }

  /**
   * Step-up as a trial move rather than a mid-sweep Y mutation: re-run the same
   * horizontal move one step height higher, and only accept it if there is
   * headroom, if it actually gains ground, and if something is there to land on.
   */
  _tryStep(startX, startZ, dx, dz, feet, colliders, resGround) {
    const len = Math.hypot(dx, dz);
    if (len < 1e-7) return false;
    const raised = feet + STEP_HEIGHT;
    if (this._ceilingBlocked(startX, startZ, feet, this._bodyH + STEP_HEIGHT)) return false;

    const b = this._resolveInto(startX + dx, startZ + dz, raised, raised + this._bodyH, colliders, this._resB);

    // Progress is measured along the attempted move and against the move's own
    // size, never as an absolute distance. The old test asked the raised move to
    // beat the ground move by a fixed 1 cm, which no stair could ever satisfy:
    // the frame the capsule touches a step, _clipVelocity zeroes the velocity
    // into it, so from then on the attempted move is a single acceleration tick
    // — 4.3 mm at 120 Hz — and 4.3 mm can never be 1 cm better than anything.
    // The player parked one tick short of the step forever, which is why the
    // stairs could only be jumped.
    const ux = dx / len, uz = dz / len;
    const projA = (resGround.x - startX) * ux + (resGround.z - startZ) * uz;
    const projB = (b.x - startX) * ux + (b.z - startZ) * uz;
    if (projB < projA - 1e-4) return false;

    // Drop back down onto the highest support under the stepped-to spot.
    let support = -Infinity, supportBox = null;
    for (let i = 0; i < colliders.length; i++) {
      const box = colliders[i];
      if (box.max.y <= support || box.max.y > raised + 1e-4 || box.max.y < feet - 0.002) continue;
      if (this._penetrationXZ(box, b.x, b.z, RADIUS)) { support = box.max.y; supportBox = box; }
    }
    if (support < 0 && feet <= 0.002) { support = 0; supportBox = null; }
    if (support === -Infinity) return false;
    // Raising has to buy something real — ground covered or height gained. Both
    // zero means we are flush against a full-height wall, and hijacking the move
    // there would only re-clip the same slide with a different normal.
    if (projB - projA < 1e-4 && support - feet < 0.01) return false;
    if (this._bodyBlockedAt(b.x, support + 0.01, b.z, this._bodyH, colliders, supportBox)) return false;

    this.position.x = b.x;
    this.position.z = b.z;
    this._addStepSmooth(support - feet);
    this.position.y = support + EYE_STAND;
    this.velocity.y = Math.max(0, this.velocity.y);
    this.grounded = true;
    this._setGround(supportBox);
    this._clipVelocity(b);
    return true;
  }

  /**
   * Registra que a posição de colisão pulou `delta` metros de degrau, para a
   * câmera pagar essa diferença ao longo de alguns quadros em vez de num só.
   * Saltos abaixo de meio centímetro são ruído de resolução de contato e seriam
   * amortecidos à toa; acima do teto não vale prometer o que a câmera não paga.
   */
  _addStepSmooth(delta) {
    if (!Number.isFinite(delta) || Math.abs(delta) < 0.005) return;
    this._stepSmooth = clamp(this._stepSmooth + delta, -STEP_SMOOTH_MAX, STEP_SMOOTH_MAX);
  }

  /** Cache the surface we are standing on so footstep audio picks the right material. */
  _setGround(box) {
    this.groundNormal.set(0, 1, 0);
    if (box === this._groundBox) return;
    this._groundBox = box;
    this._lastGroundSurface = box ? this.surfaceOfBox(box) : null;
  }

  _overlapsXZ(b) {
    return this.position.x + RADIUS > b.min.x && this.position.x - RADIUS < b.max.x &&
           this.position.z + RADIUS > b.min.z && this.position.z - RADIUS < b.max.z;
  }

  surfaceOfBox(box) {
    if (!box || !this.level) return null;
    const i = this.level.colliders.indexOf(box);
    return i >= 0 ? this.level.surfaces[i] : null;
  }

  /** Called by the weapon system when a shot is fired. */
  addRecoil(pitch, yaw) { this.recoilPitch += pitch; this.recoilYaw += yaw; }

  get eyePosition() { return this.engine.camera.position; }
  get lookDirection() {
    return new THREE.Vector3(0, 0, -1).applyQuaternion(this.engine.camera.quaternion);
  }
  get adsAmount() { return this._adsBlend; }
  get speed() { return Math.hypot(this.velocity.x, this.velocity.z); }
  get mantling() { return !!this._mantle; }
}
